"use client";
import { motion } from "framer-motion";
import Link from "next/link";
import type { ReactNode } from "react";

export type GlowColor = "cyan" | "purple" | "none";

type GlassCardProps = {
  children: ReactNode;
  href?: string;
  className?: string;
  glow?: GlowColor;
  hoverLift?: boolean;
  onClick?: () => void;
};

// Toned down from the original 0.35-opacity / 32px neon halo per client
// feedback that the homepage read as "crypto-hype" rather than enterprise-
// grade - same hover affordance (lift + soft glow), just quieter.
const GLOW_SHADOW: Record<GlowColor, string> = {
  cyan: "0 28px 56px -30px rgba(0,0,0,0.88), 0 16px 30px -24px rgba(20,124,255,0.5), 0 0 0 1px rgba(0,245,255,0.1)",
  purple: "0 28px 56px -30px rgba(0,0,0,0.88), 0 16px 30px -24px rgba(124,58,237,0.5), 0 0 0 1px rgba(230,92,255,0.08)",
  none: "0 28px 56px -30px rgba(0,0,0,0.88), 0 0 0 1px rgba(255,255,255,0.04)",
};

/**
 * Shared glassmorphism surface used across the Reqrium dashboard: frosted
 * background, hairline border with a faint blue glow, soft lift + neon glow
 * on hover. Pass `href` to render as a Next.js <Link> (e.g. Explore grid
 * cards); otherwise renders a plain element.
 */
export default function GlassCard({
  children,
  href,
  className = "",
  glow = "cyan",
  hoverLift = true,
  onClick,
}: GlassCardProps) {
  const sheenTint =
    glow === "purple"
      ? "rgba(230,92,255,0.16)"
      : glow === "none"
        ? "rgba(255,255,255,0.10)"
        : "rgba(0,212,255,0.16)";

  const content = (
    <motion.div
      onClick={onClick}
      className={`group/glass section-panel relative overflow-hidden rounded-[26px] ${className}`}
      style={{
        background: "var(--brand-surface-soft, linear-gradient(180deg, rgba(10,22,48,0.76), rgba(5,10,24,0.88)))",
        backdropFilter: "blur(var(--glass-blur, 18px))",
        WebkitBackdropFilter: "blur(var(--glass-blur, 18px))",
        border: "1px solid var(--brand-stroke, rgba(148, 163, 184, 0.14))",
        boxShadow: "var(--brand-panel-shadow, 0 24px 70px -36px rgba(0,0,0,0.9))",
      }}
      whileHover={
        hoverLift
          ? {
              y: -4,
              boxShadow: GLOW_SHADOW[glow],
              borderColor: glow === "purple" ? "rgba(230,92,255,0.22)" : "rgba(0,245,255,0.22)",
            }
          : undefined
      }
      transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Premium pass: a corner-anchored accent bloom that fades in on
          hover. Purely decorative and pointer-transparent, so it can't
          intercept clicks on the card's link/children. It's what gives the
          grid its sense of depth without adding another border or shadow
          layer competing with the existing ones. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover/glass:opacity-100"
        style={{ background: `radial-gradient(circle, ${sheenTint}, transparent 70%)` }}
      />
      {/* Top hairline highlight - reads as a lit edge on a glass panel. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-6 top-0 h-px opacity-60 transition-opacity duration-300 group-hover/glass:opacity-100"
        style={{ background: `linear-gradient(90deg, transparent, ${sheenTint}, transparent)` }}
      />
      {children}
    </motion.div>
  );

  if (href) {
    return (
      <Link
        href={href}
        className="block rounded-[26px] focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0F1C]"
      >
        {content}
      </Link>
    );
  }

  return content;
}
